import { useMemo, useState } from "react";
import { ArrowLeft, Copy, ExternalLink, Link2, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { Site } from "@/bryntum-lib/models/Site";
import { cn } from "@/modern/utils";
import { useCustomDomain } from "../hooks/useCustomDomain";
import type { DnsRecordInstruction } from "../types/domain";
import { domainStatusLabel, resolveSiteLiveUrl } from "../utils";
import { SitePublishUrlRow } from "./SitePublishUrlRow";

/** Short host many DNS panels expect (relative to the apex domain). */
function shortDnsNameHint(host: string, apexDomain?: string): string | null {
  const trimmed = host.trim();
  if (!trimmed) return null;

  const apex = apexDomain?.replace(/^https?:\/\//, "").replace(/\.$/, "").toLowerCase();
  let relative = trimmed;
  if (apex && trimmed.toLowerCase().endsWith(`.${apex}`)) {
    relative = trimmed.slice(0, -(apex.length + 1));
  } else {
    const parts = trimmed.split(".");
    if (parts.length > 2) relative = parts[0] ?? trimmed;
  }

  if (!relative || relative === trimmed) return null;
  return relative;
}

function copyText(label: string, value: string) {
  void navigator.clipboard.writeText(value);
  toast.success(`Copied ${label}`);
}

function DnsLabeledValue({
  label,
  value,
  copyable = false,
  hint,
  hintCopyValue,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  hint?: string | null;
  hintCopyValue?: string | null;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        {copyable && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            onClick={() => copyText(label, value)}
          >
            <Copy className="size-3.5" />
            Copy
          </Button>
        )}
      </div>
      <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5">
        <p className="break-all font-mono text-sm leading-relaxed text-foreground">{value}</p>
      </div>
      {hint && (
        <p className="text-xs leading-snug text-muted-foreground">
          {hint}
          {hintCopyValue && (
            <>
              {" "}
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                onClick={() => copyText("short name", hintCopyValue)}
              >
                Copy “{hintCopyValue}”
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
}

function DnsRecordCard({
  step,
  stepTotal,
  title,
  why,
  record,
  apexDomain,
}: {
  step: number;
  stepTotal: number;
  title: string;
  why: string;
  record: DnsRecordInstruction;
  apexDomain?: string;
}) {
  const shortName = shortDnsNameHint(record.host, apexDomain);

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Step {step} of {stepTotal}
        </p>
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <p className="text-sm leading-snug text-muted-foreground">{why}</p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <DnsLabeledValue label="Type" value={record.record_type || "CNAME"} />
          <DnsLabeledValue label="TTL" value={String(record.ttl ?? 300)} />
        </div>
        <DnsLabeledValue
          label="Name"
          value={record.host}
          copyable
          hint={
            shortName
              ? "Some DNS providers only want the left part of the name — not the full domain."
              : null
          }
          hintCopyValue={shortName}
        />
        <DnsLabeledValue label="Value" value={record.value} copyable />
      </div>
    </section>
  );
}

type DnsSetupItem = {
  key: string;
  title: string;
  why: string;
  record: DnsRecordInstruction;
};

function DnsSetupDialog({
  open,
  onOpenChange,
  domain,
  records,
  apexDomain,
  connectUrl,
  isReadOnly,
  busy,
  onConfirm,
  onChangeDomain,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain: string;
  records: DnsSetupItem[];
  apexDomain?: string;
  connectUrl?: string | null;
  isReadOnly: boolean;
  busy: boolean;
  onConfirm: () => void | Promise<void>;
  onChangeDomain: () => void | Promise<void>;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[min(90vh,720px)] w-[min(100vw-1.5rem,560px)] max-w-none flex-col gap-0 p-0 z-[calc(var(--swivu-bryntum-z-index)+10)]"
        dialogOverlayClassName="z-[calc(var(--swivu-bryntum-z-index)+10)]"
      >
        <DialogHeader className="shrink-0 space-y-1.5 px-5 py-4 text-left">
          <DialogTitle>Set up DNS for {domain || "your domain"}</DialogTitle>
          <DialogDescription className="text-left">
            Open your domain registrar (GoDaddy, Cloudflare, Namecheap, etc.) and add{" "}
            <span className="font-medium text-foreground">{records.length}</span>{" "}
            {records.length === 1 ? "record" : "records"}. Copy each field into the matching box.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 py-4">
          {connectUrl && !isReadOnly && (
            <Button type="button" variant="outline" className="w-full gap-1.5" asChild>
              <a href={connectUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-4" />
                Or connect automatically with your registrar
              </a>
            </Button>
          )}

          {records.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 shrink-0 animate-spin" />
              Loading DNS instructions…
            </p>
          ) : (
            records.map((item, index) => (
              <DnsRecordCard
                key={item.key}
                step={index + 1}
                stepTotal={records.length}
                title={item.title}
                why={item.why}
                record={item.record}
                apexDomain={apexDomain}
              />
            ))
          )}
        </div>

        {!isReadOnly && (
          <DialogFooter className="shrink-0 gap-2 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="gap-1.5"
              disabled={busy}
              onClick={() => void onChangeDomain()}
            >
              <ArrowLeft className="size-4" />
              Change domain
            </Button>
            <Button
              type="button"
              disabled={busy || records.length === 0}
              onClick={() => void onConfirm()}
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : "I've added DNS records"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface SitePublishDomainBlockProps {
  site: Site;
  boardSlug: string | undefined;
  boardId: number | undefined;
  isReadOnly: boolean;
  isBusy: boolean;
  onDomainChanged?: () => void;
}

export function SitePublishDomainBlock({
  site,
  boardSlug,
  boardId,
  isReadOnly,
  isBusy,
  onDomainChanged,
}: SitePublishDomainBlockProps) {
  const [setupOpen, setSetupOpen] = useState(false);
  const [dnsModalOpen, setDnsModalOpen] = useState(false);

  const {
    status,
    domainInput,
    setDomainInput,
    instructions,
    lastError,
    isBusy: domainBusy,
    handleSave,
    handleConfirm,
    handleRemove,
    handleChangeDomain,
    hasDomainApi,
  } = useCustomDomain({
    site,
    boardSlug,
    boardId,
    isReadOnly,
    onDomainChanged,
  });

  const busy = isBusy || domainBusy;
  const connectUrl = instructions?.domainConnectUrl;
  const liveDomainUrl = resolveSiteLiveUrl(site);
  const hasDnsInstructions =
    Boolean(instructions?.validationRecords.length) || Boolean(instructions?.routingCname);
  const apexDomain = site.domain?.trim() || domainInput.trim() || undefined;
  const domainLabel = site.domain?.trim() || domainInput.trim();

  const dnsRecords = useMemo(() => {
    const records: DnsSetupItem[] = [];
    if (!instructions) return records;

    instructions.validationRecords.forEach((record, index) => {
      records.push({
        key: `validation-${record.host}-${index}`,
        title:
          instructions.validationRecords.length > 1
            ? `HTTPS certificate (${index + 1})`
            : "HTTPS certificate",
        why: "Add this record so we can prove you own the domain and turn on HTTPS.",
        record,
      });
    });

    if (instructions.routingCname) {
      records.push({
        key: `routing-${instructions.routingCname.host}`,
        title: "Point domain to Swivu",
        why: "Add this record so visitors reach your site when they open your domain.",
        record: instructions.routingCname,
      });
    }

    return records;
  }, [instructions]);

  const onConfirmDns = async () => {
    const ok = await handleConfirm();
    // Stay open if verification failed or records are not detected yet.
    if (ok && site.domainStatus !== "pending_verification") {
      setDnsModalOpen(false);
    }
  };

  const onChangeDomainFromModal = async () => {
    const ok = await handleChangeDomain();
    if (ok) setDnsModalOpen(false);
  };

  if (!hasDomainApi) {
    return <p className="text-xs text-muted-foreground">Domain setup will appear when the board is ready.</p>;
  }

  const statusChip = (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium",
        status === "active" && "border-emerald-500/30 bg-emerald-500/10 text-emerald-600",
        status === "failed" && "border-destructive/30 bg-destructive/10 text-destructive",
        status === "pending_verification" && "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-200",
        status === "provisioning" && "border-primary/30 bg-primary/10 text-primary",
        status === "unset" && "border-border bg-muted/40 text-muted-foreground",
      )}
    >
      {(status === "pending_verification" || status === "provisioning") && (
        <Loader2 className="size-3 animate-spin" aria-hidden />
      )}
      {domainStatusLabel(status)}
    </span>
  );

  if (status === "active") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          {statusChip}
          {!isReadOnly && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => void handleRemove()}
              disabled={busy}
            >
              <Trash2 className="size-3" />
              Remove
            </Button>
          )}
        </div>
        <SitePublishUrlRow
          url={liveDomainUrl}
          emptyLabel="Publish live to get your domain URL"
        />
        {site.canonicalUrl?.trim() && (
          <p className="text-[11px] text-muted-foreground">
            Canonical: <span className="font-mono text-foreground/80">{site.canonicalUrl.replace(/^https?:\/\//, "")}</span>
          </p>
        )}
      </div>
    );
  }

  const showSetup = setupOpen || status === "failed" || (status === "unset" && domainInput.trim().length > 0);

  return (
    <>
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          {statusChip}
          {status === "unset" && !isReadOnly && !showSetup && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs text-primary"
              onClick={() => setSetupOpen(true)}
            >
              <Link2 className="size-3" />
              Add custom domain
            </Button>
          )}
          {!isReadOnly && (status === "provisioning" || status === "failed") && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => void handleRemove()}
              disabled={busy}
            >
              <Trash2 className="size-3" />
              Cancel
            </Button>
          )}
        </div>

        {site.domain && status !== "unset" && (
          <p className="truncate font-mono text-xs text-foreground">{site.domain}</p>
        )}

        {status === "failed" && lastError && (
          <p className="text-xs leading-relaxed text-destructive">{lastError}</p>
        )}

        {status === "provisioning" && (
          <p className="text-[11px] text-muted-foreground">Setting up SSL and routing — usually a few minutes.</p>
        )}

        {showSetup && (status === "unset" || status === "failed") && !isReadOnly && (
          <div className="space-y-2 rounded-lg border border-border/80 bg-muted/20 p-2.5">
            <p className="text-[11px] text-muted-foreground">
              Visitors will reach your site at your own hostname after DNS verification.
            </p>
            <div className="flex gap-1.5">
              <Input
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder="www.yourcompany.com"
                variant="outline"
                inputSize="sm"
                className="h-8 font-mono text-xs"
                disabled={busy}
              />
              <Button type="button" size="sm" className="h-8 shrink-0 px-3" onClick={() => void handleSave()} disabled={busy}>
                {domainBusy ? <Loader2 className="size-3.5 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        )}

        {status === "pending_verification" && (
          <div className="space-y-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-2.5">
            <p className="text-[11px] leading-relaxed text-amber-900 dark:text-amber-100">
              {dnsRecords.length > 0
                ? `Add ${dnsRecords.length} DNS ${dnsRecords.length === 1 ? "record" : "records"} at your registrar. Status updates automatically until active.`
                : "DNS records are required to finish setup. Status updates automatically until active."}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <Button
                type="button"
                size="sm"
                className="h-8 flex-1 text-xs"
                onClick={() => setDnsModalOpen(true)}
                disabled={!hasDnsInstructions}
              >
                {!instructions ? "Loading…" : hasDnsInstructions ? "View DNS records" : "No DNS records yet"}
              </Button>
              {!isReadOnly && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1 text-xs"
                  onClick={() => void handleChangeDomain()}
                  disabled={busy}
                >
                  <ArrowLeft className="size-3" />
                  Change
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <DnsSetupDialog
        open={dnsModalOpen}
        onOpenChange={setDnsModalOpen}
        domain={domainLabel}
        records={dnsRecords}
        apexDomain={apexDomain}
        connectUrl={connectUrl}
        isReadOnly={isReadOnly}
        busy={busy}
        onConfirm={onConfirmDns}
        onChangeDomain={onChangeDomainFromModal}
      />
    </>
  );
}
